import React, { useEffect } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import AboutUsImage from "../assets/AboutUs.jpg"; // Main info image

// ✅ Import your service icons
import basementIcon from "../assets/Basement-Renovations-icon.png";
import demolitionsIcon from "../assets/Demolitions-icon.png";
import electricalIcon from "../assets/Electrical-icon.png";
import flooringIcon from "../assets/Flooring&Stains-icon.png";
import kitchenIcon from "../assets/Kitchen-Renovation-icon.png";
import paintingIcon from "../assets/Painting-icon.png";
import plumbingIcon from "../assets/Plumbing-icon.png";
import washroomIcon from "../assets/Washroom-Renovation-icon.png";

import "./AboutUs.css";

const AboutUs = () => {
  useEffect(() => {
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Fade-in animation for cards
    const cards = document.querySelectorAll(".fade-card");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("fade-in");
        });
      },
      { threshold: 0.2 }
    );
    cards.forEach((card) => observer.observe(card));
    return () => cards.forEach((card) => observer.unobserve(card));
  }, []);

  // ✅ Service Cards with image icons
  const serviceCards = [
    {
      icon: <img src={kitchenIcon} alt="Kitchen Renovation" className="service-icon" />,
      title: "Kitchen Renovation",
      desc: "Modern, functional, and stylish kitchens.",
    },
    {
      icon: <img src={washroomIcon} alt="Washroom Renovation" className="service-icon" />,
      title: "Washroom Renovation",
      desc: "Elegant and efficient bathroom upgrades.",
    },
    {
      icon: <img src={flooringIcon} alt="Flooring & Stains" className="service-icon" />,
      title: "Flooring & Stains",
      desc: "Durable floors with premium finishes.",
    },
    {
      icon: <img src={basementIcon} alt="Basement Renovations" className="service-icon" />,
      title: "Basement Renovations",
      desc: "Functional and beautiful basement spaces.",
    },
    {
      icon: <img src={paintingIcon} alt="Painting" className="service-icon" />,
      title: "Painting",
      desc: "Vibrant colors for walls and ceilings.",
    },
    {
      icon: <img src={demolitionsIcon} alt="Demolitions" className="service-icon" />,
      title: "Demolitions",
      desc: "Safe and efficient structural removal.",
    },
    {
      icon: <img src={plumbingIcon} alt="Plumbing" className="service-icon" />,
      title: "Plumbing",
      desc: "Reliable installations and repairs.",
    },
    {
      icon: <img src={electricalIcon} alt="Electrical" className="service-icon" />,
      title: "Electrical",
      desc: "Safe electrical solutions for your home.",
    },
  ];

  // Advantage Cards
  const advantageCards = [
    {
      title: "Quality & Precision",
      desc: "Every project is executed with meticulous attention to detail to ensure lasting quality.",
    },
    {
      title: "Client Satisfaction",
      desc: "We prioritize your satisfaction and deliver projects that exceed expectations.",
    },
    {
      title: "Innovation",
      desc: "We implement modern solutions and designs that enhance both aesthetics and functionality.",
    },
    {
      title: "Reliable Support",
      desc: "Our team is always available to assist and ensure a stress-free renovation experience.",
    },
  ];

  return (
    <div className="aboutus-page">
      {/* ================= HERO SECTION ================= */}
      <section className="hero-section text-white text-center d-flex align-items-center">
        <Container>
          <h1 className="fw-bold mb-3">Ontario Home Renovations</h1>
          <p className="lead">
            Transforming homes into beautiful, functional, and modern living spaces.
          </p>
        </Container>
      </section>

      {/* ================= INFO SECTION ================= */}
      <section className="info-section py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="mb-4 mb-md-0">
              <img
                src={AboutUsImage}
                alt="Kitchen Renovation in Progress"
                className="img-fluid rounded shadow"
              />
            </Col>
            <Col md={6}>
              <h2 className="fw-bold mb-3">Expert Renovation Solutions You Can Trust</h2>
              <p>
                We understand that your home is your sanctuary. Our dedicated team ensures each renovation project enhances functionality, aesthetics, and value.
              </p>
              <p>
                Our mission is to provide homeowners with exceptional service, modern design, and quality craftsmanship. We turn your vision into reality with care and precision.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= SERVICE CARDS ================= */}
      <section id="services" className="service-cards-section py-5">
        <Container>
          <h2 className="section-heading text-center mb-5">
            Our Comprehensive Renovation Services
          </h2>
          <Row className="g-4 justify-content-center">
            {serviceCards.map((card, idx) => (
              <Col md={6} lg={3} key={idx}>
                <Card className="modern-card text-center h-100 p-4 border-0">
                  <div className="modern-icon mb-3">{card.icon}</div>
                  <h5 className="modern-title mb-2">{card.title}</h5>
                  <p className="modern-desc mb-0">{card.desc}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ================= ADVANTAGE SECTION ================= */}
      <section className="advantage-section py-5 bg-light">
        <Container>
          <h2 className="section-heading text-center mb-4">
            Why Choose Ontario Home Renovations
          </h2>
          <Row className="g-4">
            {advantageCards.map((card, idx) => (
              <Col md={6} key={idx}>
                <Card className="fade-card h-100 p-4">
                  <Card.Title className="card-title">{card.title}</Card.Title>
                  <Card.Text>{card.desc}</Card.Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ================= STATS SECTION ================= */}
      <section className="about-stats py-5 text-white text-center">
        <Container>
          <Row>
            <Col md={4}>
              <h2>500+</h2>
              <p>Homes Renovated</p>
            </Col>
            <Col md={4}>
              <h2>10+</h2>
              <p>Years of Experience</p>
            </Col>
            <Col md={4}>
              <h2>100%</h2>
              <p>Client Satisfaction</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section className="cta-section py-5 text-white text-center">
        <Container>
          <h2 className="fw-bold mb-3">Ready to Transform Your Home?</h2>
          <p className="mb-4">
            Let Ontario Home Renovations create a customized renovation plan that fits your style and budget.
          </p>
          <Button
            as="a"
            href="https://cal.com/onhomereno"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cta px-4 py-2"
          >
            Request a Free Quote
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default AboutUs;
